import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Document {
  id: string
  name: string
  file_type: string
  category: string
  folder: string
  size_kb: number
  version: string
  owner: string
  department: string
  modified_date: string
  is_locked: boolean
}

interface Folder {
  id: string
  name: string
  path: string
  document_count: number
}

export default function DocumentsPage() {
  const [documents, setDocuments] = useState<Document[]>([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [docRes, folderRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/documents/'),
      fetch(API_BASE + '/documents/folders'),
      fetch(API_BASE + '/documents/statistics')
    ])
    if (docRes.ok) setDocuments((await docRes.json()).documents || [])
    if (folderRes.ok) setFolders((await folderRes.json()).folders || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Document Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Upload Document</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Documents</div>
              <div className="text-2xl font-bold">{statistics.total_documents}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Storage Used</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_storage_mb} MB</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Shares</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_shares}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Views</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.total_views}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Storage Used %</div>
              <div className="text-2xl font-bold">{statistics.storage_used_percent}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Folders</h3>
            <div className="space-y-2">
              {folders.map(folder => (
                <div key={folder.id} className="p-3 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer">
                  <div className="font-medium text-sm">{folder.name}</div>
                  <div className="text-xs text-gray-500">{folder.document_count} files</div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-3 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Documents</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modified</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {documents.slice(0, 15).map(doc => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3">
                      <div className="font-medium text-sm">{doc.name}</div>
                      <div className="text-xs text-gray-500">{doc.folder} - v{doc.version}</div>
                    </td>
                    <td className="px-6 py-3">
                      <div className="text-sm">{doc.owner}</div>
                      <div className="text-xs text-gray-500">{doc.department}</div>
                    </td>
                    <td className="px-6 py-3 text-sm">{(doc.size_kb / 1024).toFixed(1)} MB</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{doc.modified_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
